import React from 'react';
import {Button, ButtonGroup, ButtonToolbar, Input, InputGroup, InputGroupText, Label} from "reactstrap";
import TextLabel from "../../controls/TextLabel.mjs";
import { Glob as G } from "../../libs/Glob.mjs";
import EditorSubdHeaderFooter from "./EditorSubdHeaderFooter.mjs";
import EditorSubIndentDepends from "./EditorSubIndentDepends.mjs";
import MyAccordion from "../../controls/MyAccordion.mjs";
import HamburgerArea from "../../controls/HamburgerArea.mjs";
import TrashIcon from "../../media/images/TrashIcon.mjs";
import CheckIcon from "../../media/images/CheckIcon.mjs";
import ChevronUpIcon from "../../media/images/ChevronUpIcon.mjs";
import ChevronDownIcon from "../../media/images/ChevronDownIcon.mjs";
import AddAfterIcon from "../../media/images/AddAfterIcon.mjs";
import ChevronTopIcon from "../../media/images/ChevronTopIcon.mjs";
import ChevronBottomIcon from "../../media/images/ChevronBottomIcon.mjs";
import RevertIcon from "../../media/images/RevertIcon.mjs";
import adminLib from "../../libs/adminLib.mjs";

const EditorAbstractField = class extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			value: props.value ? G.clone(props.value) : {},
			changes: 0,
			dependants: (props.value?.dependants || []).length
		};
		this.field = this.field.bind(this);
		this.setValue = this.setValue.bind(this);
		this.isValid = this.isValid.bind(this);
		this.commit = this.commit.bind(this);
		this.preFilterValue = this.preFilterValue.bind(this);
		this.uid = this.state.value?.uuid || G.generateUid('ufield');
		this.state.open = !! G.getStatus('fieldsOpen')[ this.uid ];
		this.textareaField = this.textareaField.bind(this);
	}

	/**
	 * Returns the editor for a textarea, multiline field.
	 * @desc Defaults to the field "label"
	 * @param {string} [type] Defaults to "label"
	 * @param {string} [label] Defaults to "Label"
	 * @return {JSX.Element}
	 */
	textareaField( type = 'label', label = 'Label') {
		return(
			<InputGroup>
				<InputGroupText>
					<TextLabel value={ label } />
				</InputGroupText>
				<Input
					type={"textarea"}
					rows={1}
					value={ (this.state.value||{})[type] || '' }
					onChange={ e => {
						this.setValue(type, e.target.value);
					}}
				/>
			</InputGroup>
		);
	}

	/**
	 * Set the value of a field, deletes it if value is undefined
	 * @param {string} k Field name
	 * @param {any} [v] Value
	 */
	async setValue(k,v) {
		const value = this.state.value;
		if ( typeof v === 'undefined') {
			delete value[k];
		} else {
			value[k] = v;
		}
		await this.setState({ value: value, changes: this.state.changes +1 });
	}

	/**
	 * @return {boolean}
	 */
	isValid() {
		return true;
	}

	/**
	 * @return {string|JSX.Element}
	 * @desc Field to be overwritten in derived classes
	 * @desc Contains field type specific entries
	 */
	field() {
		return 'AbstractField';
	}

	/**
	 * @private
	 * @return {any}
	 */
	localFilterValue() {
		return this.state.value;
	}

	preFilterValue() {
		const v = this.localFilterValue();
		if ( v.header ) {
			if ( v.header?.node === 'hr') {
				delete v.header.text;
			} else if ( ! v.header.text?.length ) {
				delete v.header;
			}
		}
		if ( v.footer ) {
			if ( v.footer?.node === 'hr') {
				delete v.footer.text;
			} else if ( ! v.footer.text?.length ) {
				delete v.footer;
			}
		}
		if ( v.key === 'format') delete v.key;
		return v;
	}

	/**
	 * Commit changes to form
	 * @param {Event} [evt] An optional event triggering the change, so we can prevent default
	 * @return {void}
	 */
	commit( evt ) {
		if ( evt && evt instanceof Event ) try { evt.preventDefault(); } catch( e ) {}
		if ( ! this.isValid() ) return;
		this.setState( { changes: 0 }, ()=>{
			this.props.commit( this.preFilterValue() );
		});
	}

	render() {
		const ft = this.props.value.type ?? 'format';
		const fd = this.props.fieldTypes.find(t => (t.type === ft) );
		return(
			<div
				className={"field " + ( this.state.changes ? 'changed ' : 'unchanged ' )+(this.props.value?.key ? 'key_'+this.props.value?.key : 'nokey')}
				rel={ "field-" + (this.state.value?.key || this.props.index) }
				id={ this.uid }
			>
				<MyAccordion
					open={ this.state.open }
					onToggle={ (obj,mode) => {
						const o = G.getStatus('fieldsOpen') || {};
						o[this.uid] = mode;
						this.setState( { open: mode },()=>{
							G.setStatus('fieldsOpen',o );
						});
					}}
					title={(
						<div className={"fieldControls"}>
							<div className={"fieldType"}>
								<span>{this.props.value?.key ? this.props.value.key : '—'}</span>
								<span>{this.props.value?.type ? fd?.name : 'Format'}</span>
								<span>{ this.state.dependants || '' }</span>
								<span>{(this.state.value?.depends || []).length || ''}</span>
							</div>
							<div
								onClick={ event => { event.stopPropagation(); } }
							>
								<ButtonGroup className={"btn-icon me-2"}>
									<Button
										disabled={!!this.state.changes || typeof this.props.moveUp !== 'function'}
										color={"secondary"}
										onClick={this.props.moveUp}
									>
										<ChevronUpIcon />
									</Button>
									<Button
										disabled={!!this.state.changes || typeof this.props.moveDown !== 'function'}
										color={"secondary"}
										onClick={this.props.moveDown}
									>
										<ChevronDownIcon />
									</Button>
								</ButtonGroup>
								<ButtonGroup
									className={"me-2 btn-icon"}
									disabled={!this.state.changes}
								>
									<Button
										disabled={!this.state.changes}
										color={"success"}
										title={G.label('Save')}
										onClick={this.commit}
									>
										<CheckIcon />
									</Button>
								</ButtonGroup>
							</div>
							<div>
								<HamburgerArea align={"end"}>
									<ButtonToolbar className={"flex-column"}>
										<ButtonGroup
											className={"btn-icon"}
											vertical={true}
											disabled={!this.state.changes}
										>
											<Button
												disabled={!this.state.changes}
												color={"success"}
												onClick={this.commit}
											>
												<i>
													<CheckIcon />
												</i>
												<b>
													<TextLabel value={"Save"} />
												</b>
											</Button>
											<Button
												disabled={!this.state.changes}
												color={"warning"}
												onClick={() => {
													this.setState({
														value: this.props.value ? G.clone(this.props.value) : {},
														changes: 0
													});
												}}
											>
												<i>
													<RevertIcon />
												</i>
												<b>
													<TextLabel value={"Cancel"} />
												</b>
											</Button>
										</ButtonGroup>
										<ButtonGroup
											className={"btn-icon mt-2"}
											vertical={true}
										>
											<Button
												color={"secondary"}
												disabled={ !!this.state.changes || typeof this.props.moveTop !== 'function' }
												onClick={ this.props.moveTop }
											>
												<i>
													<ChevronTopIcon />
												</i>
												<b>
													<TextLabel value={"Move to top"} />
												</b>
											</Button>
											<Button
												color={"secondary"}
												disabled={ !!this.state.changes || typeof this.props.moveBottom !== 'function' }
												onClick={ this.props.moveBottom }
											>
												<i>
													<ChevronBottomIcon />
												</i>
												<b>
													<TextLabel value={"Move to bottom"} />
												</b>
											</Button>
										</ButtonGroup>
										<ButtonGroup
											className={"btn-icon mt-2"}
											vertical={true}
										>
											<Button
												disabled={ typeof this.props.insertAfter !== 'function'}
												color={"primary"}
												onClick={ this.props.insertAfter }
											>
												<i>
													<AddAfterIcon />
												</i>
												<b>
													<TextLabel value={"Add after…"} />
												</b>
											</Button>
										</ButtonGroup>
										<ButtonGroup
											className={"btn-icon mt-2"}
											vertical={true}
										>
											<Button
												disabled={ !! this.state.dependants }
												color={"danger"}
												onClick={ ()=>{
													const p = {};
													if ( this.state.value?.key ) p.item = `“${ this.state.value.key }”`;
													if ( window.confirm( G.label('ConfirmItemDeletion', p))) {
														this.props.commit(undefined, this.props.index);
													}
												}}
											>
												<i>
													<TrashIcon />
												</i>
												<b>
													<TextLabel value={"Remove"} />
												</b>
											</Button>
										</ButtonGroup>
									</ButtonToolbar>
								</HamburgerArea>
							</div>
						</div>
					)}
				>
					<div className={"fieldRows"}>
						{ this.state.dependants === 0 ? '' : (
							<div className={"dependants"}>
								<TextLabel value={"DependOnItem"} params={{item:`“${this.props.value.key}”`}} />: 
								{ this.props.value.dependants.map((d,i) => (
									<span key={i} onClick={ ()=> { adminLib.editorScrollToFieldKey(d) }}>{d}</span>
								))}
							</div>
						) }
						<EditorSubIndentDepends
							key={'deps' + this.state.changes}
							fieldKeys={this.props.fieldKeys}
							value={this.state.value}
							setValue={this.setValue}
						/>
						<EditorSubdHeaderFooter
							key={'header' + (this.state.changes ? 'c' : 'x')}
							value={this.state.value}
							selector={'header'}
							setValue={this.setValue}
						/>
						{fd?.nokey ? '' : (
							<InputGroup>
								<InputGroupText>
									<TextLabel value={"Node ID"}/>
								</InputGroupText>
								<Input
									type="text"
									disabled={ this.state.dependants > 0 }
									value={this.state.value.key || ''}
									placeholder={"⚠️ Se manca il nome del nodo il campo non viene salvato"}
									onChange={e => {
										this.setValue('key', e.currentTarget.value);
									}}
								/>
								<InputGroupText>
									<Input
										id={this.uid + '-required'}
										type={"checkbox"}
										disabled={!this.state.value?.key}
										checked={!!this.state.value?.required}
										onChange={e => {
											this.setValue('required', e.currentTarget.checked || undefined);
										}}
									/>
								</InputGroupText>
								<InputGroupText
									disabled={!this.state.value?.key}
								>
									<Label htmlFor={this.uid + '-required'}>Required</Label>
								</InputGroupText>
							</InputGroup>
						)}
						{this.field()}
						<EditorSubdHeaderFooter
							key={'footer' + (this.state.changes ? 'c' : 'x')}
							value={this.state.value}
							selector={'footer'}
							setValue={this.setValue}
						/>
					</div>
				</MyAccordion>
			</div>
		);
	}
}

export default EditorAbstractField;
